import { useLocaleStore } from "@/lib/stores/localeStore";
import { cn } from "@/lib/utils";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { LuCaptions, LuCaptionsOff } from "react-icons/lu";
import {
  useExternalSubtitles,
  type ExternalSubtitleTrack,
} from "@/hooks/useExternalSubtitles";

type SubtitleTrack = ExternalSubtitleTrack;

export interface VideoWithSubtitlesRef {
  video: HTMLVideoElement | null;
  resetVideo: () => void;
}

interface VideoWithSubtitlesProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  poster?: string;
  subtitleTracks: SubtitleTrack[];
  className?: string;
  videoClassName?: string;
  captionsContainerClassName?: string;
  captionsTextClassName?: string;
  showCaptionsToggle?: boolean;
  defaultCaptionsVisible?: boolean;
  disablePictureInPicture?: boolean;
  disableRemotePlayback?: boolean;
  controlsList?: string;
  dir?: "ltr" | "rtl";
  activeSubtitleLanguage?: string;
  videoContainerClass?: string;
}

export const VideoWithSubtitles = forwardRef<VideoWithSubtitlesRef, VideoWithSubtitlesProps>(({
  src,
  poster,
  subtitleTracks,
  className = "",
  videoClassName = "w-full h-auto rounded-xl",
  captionsContainerClassName = "w-full max-w-4xl mx-auto rounded-xl bg-black/85 p-3",
  captionsTextClassName = "text-center text-sm font-medium text-white/80",
  showCaptionsToggle = true,
  defaultCaptionsVisible = true,
  disablePictureInPicture = true,
  disableRemotePlayback = true,
  controlsList = "nodownload",
  dir: initialDir = "rtl",
  activeSubtitleLanguage,
  videoContainerClass
}, ref) => {
  const [showExternalCaptions, setShowExternalCaptions] = useState(defaultCaptionsVisible);
  const [dirState, setDirState] = useState<"ltr" | "rtl">(initialDir);

  const videoRef = useRef<HTMLVideoElement>(null);
  const locale = useLocaleStore((state) => state.locale);
  const { cueText, setCueText } = useExternalSubtitles({
    videoRef,
    subtitleTracks,
    activeSubtitleLanguage,
  });


  useEffect(() => {
    setDirState(locale === "ar" ? "rtl" : "ltr");
  }, [locale]);

  // Cleanup function to reset video
  const resetVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setCueText("");
  };

  useImperativeHandle(ref, () => ({
    video: videoRef.current,
    resetVideo
  }), []);

  return (
    <div className={`relative ${className}`}>
      <div className={cn("mb-6 w-full max-w-4xl mx-auto rounded-xl overflow-hidden shadow-lg relative", videoContainerClass)}>
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          controls
          playsInline // for iOS inline playback
          className={videoClassName}
          disablePictureInPicture={disablePictureInPicture}
          disableRemotePlayback={disableRemotePlayback}
          controlsList={controlsList}
        >
          {subtitleTracks.map((track, index) => (
            <track
              key={index}
              kind="subtitles"
              src={track.src}
              srcLang={track.srcLang}
              label={track.label}
              default={track.default}
            />
          ))}
        </video>

        {showCaptionsToggle && (
          <button
            type="button"
            onClick={() => setShowExternalCaptions((s) => !s)}
            className="
              absolute right-3 top-3 z-10 rounded-full bg-white/90
              hover:bg-white shadow p-2 transition
            "
            aria-pressed={showExternalCaptions}
            aria-label={showExternalCaptions ? "Hide captions" : "Show captions"}
          >
            {showExternalCaptions ? (
              <LuCaptions className="h-5 w-5" aria-hidden />
            ) : (
              <LuCaptionsOff className="h-5 w-5" aria-hidden />
            )}
          </button>
        )}
      </div>

      {showExternalCaptions && cueText && (
        <div className={captionsContainerClassName} dir={dirState}>
          <p
            className={captionsTextClassName}
            style={{ fontFamily: "'Noto Sans Arabic', sans-serif" }}
          >
            {cueText}
          </p>
        </div>
      )}
    </div>
  );
});

VideoWithSubtitles.displayName = "VideoWithSubtitles";

export default VideoWithSubtitles;
